import React from "react";
import { ArrowRight, Ruler, House, Building2, Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "item-1",
  title: "Large Format Porcelain",
  icon: <Ruler className="w-5 h-5 text-black" />,
  description: "Specialists in installing oversized porcelain tiles with minimal grout lines for a seamless, high-end finish that expands the visual scale of any room."
}, {
  id: "item-2",
  title: "Wet Room Specialists",
  icon: <Building2 className="w-5 h-5 text-black" />,
  description: "Complete waterproof tanking systems and walk-in shower installations designed for longevity, using industry-leading materials and technical precision."
}, {
  id: "item-3",
  title: "Precision Mitred Edges",
  icon: <House className="w-5 h-5 text-black" />,
  description: "Expert 45-degree mitred tile edges for a clean, professional look without the need for metal trims, perfect for luxury bathroom niches and corners."
}, {
  id: "item-4",
  title: "Epoxy Grout Finishing",
  icon: <Leaf className="w-5 h-5 text-black" />,
  description: "Durable, stain-resistant epoxy grouting solutions that maintain their pristine appearance in high-moisture environments and heavy-traffic areas."
}];
const projects = [{
  title: "Chelsea Porcelain Suite",
  location: "Chelsea, London",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/residential_project_1.png",
  description: "A full bathroom refurbishment featuring floor-to-ceiling large format porcelain and integrated LED lighting."
}, {
  title: "Kensington Wet Room",
  location: "Kensington",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/residential_project_2.png",
  description: "A modern walk-in shower installation with a bespoke tanking system and precision mitred tile edges."
}, {
  title: "Richmond Kitchen",
  location: "Richmond",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/residential_project_3.png",
  description: "Intricate mosaic splashback and high-durability floor tiling for a premium residential kitchen renovation."
}, {
  title: "Canary Wharf Commercial",
  location: "Canary Wharf",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/residential_project_4.png",
  description: "Industrial-scale floor tiling for a luxury commercial lobby, focusing on durability and aesthetic impact."
}];
export default function ResidentialServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="38980" id="gallery" className="relative bg-background text-black py-24 overflow-hidden">
      <style>{`:root { --background: hsl(0 0% 100%); } body { text-align: left; } .container { margin-left: 0; margin-right: auto; } .flex, .grid { justify-content: flex-start; }`}</style>
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <svg className="absolute top-0 left-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          <path d="M0 100 Q 250 250 500 100 T 1000 100" fill="none" stroke="currentColor" strokeWidth="1" className="text-primary opacity-30" />
          <circle cx="80%" cy="20%" r="150" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-primary opacity-20" />
        </svg>
      </div>

      <div className="container ml-0 mr-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {}
          <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 text-black leading-tight">
              Exquisite Tiling <br />
              <span className="text-black italic">& Renovations</span>
            </h2>
            <p className="font-sans text-lg text-black mb-10 max-w-xl leading-relaxed">
              SimProTiling brings 11 years of precision to every surface in London. From large-format porcelain to bespoke wet room installations, we define luxury through technical excellence and precision workmanship.
            </p>

            <Accordion type="single" collapsible className="w-full space-y-2">
              {services.map((service, i) => <AccordionItem key={service.id} value={service.id} className="border-b border-border px-2" data-index={i}>
                  <AccordionTrigger className="hover:no-underline py-5 group">
                    <div className="flex items-center gap-4 text-left">
                      <span className="p-2 rounded-full bg-accent group-hover:bg-black/10 transition-colors duration-300">
                        {service.icon}
                      </span>
                      <span className="text-xl font-serif font-medium text-black group-hover:text-black/70 transition-colors">
                        {service.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-black text-base pl-[3.25rem] pb-6">
                    {service.description}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className={`transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent className="-ml-4">
                {projects.map((project, i) => <CarouselItem key={i} className="pl-4 md:basis-1/1 lg:basis-5/6" data-index={i}>
                    <Card className="bg-card backdrop-blur-md border border-border rounded-none overflow-hidden group hover:border-primary transition-all duration-500 h-full shadow-md hover:shadow-xl">
                      <CardContent className="p-0 relative aspect-[4/5] md:aspect-[4/3] lg:aspect-[3/4]">
                        <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
                        
                        <div className="absolute bottom-0 left-0 w-full p-8 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                          <div className="mb-4">
                            <p className="text-sm font-medium tracking-widest uppercase mb-2 !text-[hsl(0,0%,100%)]">
                              {project.location}
                            </p>
                            <h3 className="text-3xl font-serif mb-2 !text-[hsl(0,0%,100%)]">
                              {project.title}
                            </h3>
                            <p className="text-black text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                              {project.description}
                            </p>
                          </div>
                          
                          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none w-full md:w-auto">
                            <Link to="#" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-9 px-4 py-2 has-[>svg]:px-3 text-primary-foreground rounded-none w-full !bg-[hsl(210,17%,98%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 md:w-auto">
                              View Project <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>)}
              </CarouselContent>
              <div className="flex justify-start gap-2 mt-6 pr-4">
                <CarouselPrevious className="static translate-y-0 bg-transparent border-black/50 text-black hover:bg-black hover:text-white rounded-none" />
                <CarouselNext className="static translate-y-0 bg-transparent border-black/50 text-black hover:bg-black hover:text-white rounded-none" />
              </div>
            </Carousel>
          </div>
        </div>
      </div>
    </section>;
}